/**
 * Security Dashboard — scanează vault-ul client-side și raportează:
 *   - parole slabe (zxcvbn score ≤ 1)
 *   - duplicate (hash SHA-256 peste parolă match 2+)
 *   - apariții în breach-uri (HIBP k-anonymity)
 *   - credentiale expirate (expires_at trecut)
 *   - credentiale vechi (nu au fost actualizate de peste 180 zile)
 *
 * Totul 100% client-side. Parolele nu părăsesc browserul; HIBP vede
 * doar SHA1[0..5] prefix.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import * as zxcvbnCommon from '@zxcvbn-ts/language-common';
import * as zxcvbnEn from '@zxcvbn-ts/language-en';

import { credentialsApi, type CredentialSummary } from '@/api/credentials';
import {
  credentialSchemasApi,
  primarySecretKey,
  type CredentialTypeSchemaResponse,
} from '@/api/credentialSchemas';
import { checkPasswordPwned } from '@/crypto/hibp';
import { decryptCredential, type CredentialPayload } from '@/crypto/sodium';
import { formatDate } from '@/lib/format';
import { useAuthStore } from '@/stores/auth';
import StatPill from '@/components/ui/StatPill';

let zxcvbnReady = false;
function ensureZxcvbnInit() {
  if (zxcvbnReady) return;
  zxcvbnOptions.setOptions({
    translations: zxcvbnEn.translations,
    graphs: zxcvbnCommon.adjacencyGraphs,
    dictionary: { ...zxcvbnCommon.dictionary, ...zxcvbnEn.dictionary },
  });
  zxcvbnReady = true;
}

interface Finding {
  cred: CredentialSummary;
  issues: Array<
    | { kind: 'weak'; score: number }
    | { kind: 'duplicate'; with: string[] }
    | { kind: 'breach'; count: number }
    | { kind: 'expired' }
    | { kind: 'old'; days: number }
  >;
}

interface ScanState {
  progress: { done: number; total: number; currentLabel: string };
  findings: Finding[];
  weakCount: number;
  duplicateCount: number;
  breachCount: number;
  expiredCount: number;
  oldCount: number;
  finished: boolean;
}

const OLD_THRESHOLD_DAYS = 180;

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function SecurityDashboardPage() {
  const { t } = useTranslation();
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const keyPair = useAuthStore((s) => s.keyPair);

  const list = useQuery({
    queryKey: ['credentials', tenantId],
    queryFn: () => credentialsApi.list(tenantId!),
    enabled: !!tenantId,
  });
  const schemas = useQuery({
    queryKey: ['credential-schemas', tenantId],
    queryFn: () => credentialSchemasApi.list(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
  const schemaByType = useMemo(() => {
    const m: Record<string, CredentialTypeSchemaResponse> = {};
    for (const s of schemas.data ?? []) m[s.type] = s;
    return m;
  }, [schemas.data]);

  const [scan, setScan] = useState<ScanState | null>(null);
  const [running, setRunning] = useState(false);

  const runScan = async () => {
    if (!list.data || !keyPair) return;
    ensureZxcvbnInit();
    setRunning(true);

    const candidates = list.data.filter((c) => {
      const secretKey = primarySecretKey(schemaByType[c.type]);
      return secretKey !== null;
    });
    const total = candidates.length;

    const decryptedMap = new Map<string, string>(); // cred.id → parola
    const findingsByCredId = new Map<string, Finding>();
    const now = Date.now();

    setScan({
      progress: { done: 0, total, currentLabel: t('admin_ui.security.progress_starting') },
      findings: [],
      weakCount: 0,
      duplicateCount: 0,
      breachCount: 0,
      expiredCount: 0,
      oldCount: 0,
      finished: false,
    });

    // Pass 1: decryptare + zxcvbn + expired/old (rapid, fără network)
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      setScan((s) =>
        s
          ? {
              ...s,
              progress: { done: i, total, currentLabel: t('admin_ui.security.progress_decrypting', { title: c.title }) },
            }
          : s,
      );
      const issues: Finding['issues'] = [];
      try {
        const detail = await credentialsApi.get(c.id, { noTrack: true });
        const payload = (await decryptCredential(
          {
            ciphertext: detail.ciphertext,
            nonce: detail.nonce,
            dek_wrapped: detail.dek_wrapped,
          },
          keyPair,
        )) as CredentialPayload;
        const key = primarySecretKey(schemaByType[c.type]);
        const secret = key && typeof payload[key] === 'string'
          ? (payload[key] as string)
          : '';
        if (secret) {
          decryptedMap.set(c.id, secret);
          const result = zxcvbn(secret);
          if (result.score <= 1) {
            issues.push({ kind: 'weak', score: result.score });
          }
        }
      } catch {
        // decrypt eșuat — skip silent
      }

      if (c.expires_at && new Date(c.expires_at).getTime() < now) {
        issues.push({ kind: 'expired' });
      }
      const ageDays = Math.floor(
        (now - new Date(c.updated_at).getTime()) / (1000 * 60 * 60 * 24),
      );
      if (ageDays >= OLD_THRESHOLD_DAYS) {
        issues.push({ kind: 'old', days: ageDays });
      }

      if (issues.length > 0) {
        findingsByCredId.set(c.id, { cred: c, issues });
      }
    }

    // Pass 2: duplicate detection (SHA-256 hash client-side)
    const hashToIds = new Map<string, string[]>();
    for (const [id, secret] of decryptedMap) {
      const h = await sha256Hex(secret);
      if (!hashToIds.has(h)) hashToIds.set(h, []);
      hashToIds.get(h)!.push(id);
    }
    for (const ids of hashToIds.values()) {
      if (ids.length >= 2) {
        const titles = ids
          .map((i) => list.data!.find((c) => c.id === i)?.title ?? i.slice(0, 8))
          .filter(Boolean);
        for (const id of ids) {
          const cred = list.data!.find((c) => c.id === id)!;
          const others = titles.filter(
            (tt) => tt !== cred.title,
          );
          const existing = findingsByCredId.get(id) ?? { cred, issues: [] };
          existing.issues.push({ kind: 'duplicate', with: others });
          findingsByCredId.set(id, existing);
        }
      }
    }

    // Pass 3: HIBP batch (cu delay mic pt politeness)
    let hibpIdx = 0;
    for (const [id, secret] of decryptedMap) {
      hibpIdx += 1;
      setScan((s) =>
        s
          ? {
              ...s,
              progress: {
                done: total + hibpIdx,
                total: total + decryptedMap.size,
                currentLabel: t('admin_ui.security.progress_hibp', {
                  done: hibpIdx,
                  total: decryptedMap.size,
                }),
              },
            }
          : s,
      );
      try {
        const result = await checkPasswordPwned(secret);
        if (result.pwned) {
          const cred = list.data!.find((c) => c.id === id)!;
          const existing = findingsByCredId.get(id) ?? { cred, issues: [] };
          existing.issues.push({ kind: 'breach', count: result.count });
          findingsByCredId.set(id, existing);
        }
      } catch {
        // HIBP indisponibil — skip
      }
      // delay 150ms între request-uri (HIBP public, nu abuzăm)
      if (hibpIdx < decryptedMap.size) {
        await new Promise((r) => setTimeout(r, 150));
      }
    }

    const findings = Array.from(findingsByCredId.values());
    const weakCount = findings.filter((f) =>
      f.issues.some((i) => i.kind === 'weak'),
    ).length;
    const duplicateCount = findings.filter((f) =>
      f.issues.some((i) => i.kind === 'duplicate'),
    ).length;
    const breachCount = findings.filter((f) =>
      f.issues.some((i) => i.kind === 'breach'),
    ).length;
    const expiredCount = findings.filter((f) =>
      f.issues.some((i) => i.kind === 'expired'),
    ).length;
    const oldCount = findings.filter((f) =>
      f.issues.some((i) => i.kind === 'old'),
    ).length;

    setScan({
      progress: { done: total, total, currentLabel: t('admin_ui.security.progress_done') },
      findings,
      weakCount,
      duplicateCount,
      breachCount,
      expiredCount,
      oldCount,
      finished: true,
    });
    setRunning(false);
  };

  const overallScore = useMemo(() => {
    if (!scan || !list.data) return null;
    // Scor simplu 0-100: 100 - (probleme / credentiale * 100), capped la 0.
    const totalCreds = list.data.filter((c) =>
      primarySecretKey(schemaByType[c.type]),
    ).length;
    if (totalCreds === 0) return 100;
    const penalty =
      scan.weakCount * 3 +
      scan.duplicateCount * 2 +
      scan.breachCount * 8 +
      scan.expiredCount * 4 +
      scan.oldCount * 1;
    const raw = 100 - Math.round((penalty / (totalCreds * 8)) * 100);
    return Math.max(0, Math.min(100, raw));
  }, [scan, list.data, schemaByType]);

  if (!tenantId || !keyPair) {
    return <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('vault.no_tenant')}</p>;
  }

  const totalCreds = list.data
    ? list.data.filter((c) => primarySecretKey(schemaByType[c.type])).length
    : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="display-title">{t('admin_ui.security.title')}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            {t('admin_ui.security.subtitle')}
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={runScan}
          disabled={running || !list.data || list.data.length === 0}
        >
          {running
            ? t('admin_ui.security.scanning')
            : scan?.finished
              ? t('admin_ui.security.rescan')
              : t('admin_ui.security.start_scan')}
        </button>
      </div>

      {running && scan && (
        <div
          className="mb-6 rounded-lg p-4"
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
          }}
        >
          <div
            className="text-[11px] font-mono mb-2 flex justify-between"
            style={{ color: 'var(--text-muted)' }}
          >
            <span>{scan.progress.currentLabel}</span>
            <span>
              {scan.progress.done} / {scan.progress.total}
            </span>
          </div>
          <div
            className="h-1.5 rounded-full overflow-hidden"
            style={{ background: 'var(--strength-empty)' }}
          >
            <div
              className="h-full transition-all"
              style={{
                width: `${scan.progress.total > 0 ? (scan.progress.done / scan.progress.total) * 100 : 0}%`,
                background: 'var(--grad-mint)',
              }}
            />
          </div>
        </div>
      )}

      {scan?.finished && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <StatPill
              label={t('admin_ui.security.stat_score')}
              value={overallScore ?? '—'}
              color={
                overallScore == null || overallScore >= 80
                  ? 'mint'
                  : overallScore >= 50
                    ? 'amber'
                    : 'rose'
              }
            />
            <StatPill label={t('admin_ui.security.stat_weak')} value={scan.weakCount} color="amber" />
            <StatPill label={t('admin_ui.security.stat_duplicates')} value={scan.duplicateCount} color="purple" />
            <StatPill label={t('admin_ui.security.stat_breach')} value={scan.breachCount} color="rose" />
            <StatPill label={t('admin_ui.security.stat_expired')} value={scan.expiredCount} color="cyan" />
          </div>

          {scan.findings.length === 0 ? (
            <div
              className="rounded-lg p-8 text-center"
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
              }}
            >
              <div
                className="text-2xl mb-2"
                style={{ color: 'var(--c-mint)' }}
              >
                ✓
              </div>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {t('admin_ui.security.no_issues', { count: totalCreds })}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {scan.findings.map((f) => (
                <FindingCard key={f.cred.id} finding={f} />
              ))}
            </div>
          )}
        </>
      )}

      {!scan && !running && (
        <div
          className="rounded-lg p-8 text-center"
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            <Trans
              i18nKey="admin_ui.security.start_hint"
              count={totalCreds}
              components={{ strong: <strong /> }}
            />
          </p>
          <p className="text-[11px] mt-2 font-mono" style={{ color: 'var(--text-dim)' }}>
            {t('admin_ui.security.pipeline_hint')}
          </p>
        </div>
      )}
    </div>
  );
}

function FindingCard({ finding }: { finding: Finding }) {
  const severity = finding.issues.some((i) => i.kind === 'breach')
    ? 'rose'
    : finding.issues.some((i) => i.kind === 'weak' || i.kind === 'expired')
      ? 'amber'
      : 'purple';

  return (
    <Link
      to={`/vault?open=${finding.cred.id}`}
      className="block rounded-lg p-3 transition-colors"
      style={{
        background: 'var(--bg-elevated)',
        border: `1px solid var(--border)`,
        borderLeft: `3px solid var(--c-${severity})`,
        color: 'var(--text)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--bg-elevated)';
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{finding.cred.title}</div>
          <div
            className="text-[11px] mt-0.5 font-mono"
            style={{ color: 'var(--text-dim)' }}
          >
            {finding.cred.type} ·{' '}
            {formatDate(finding.cred.updated_at)}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center justify-end">
          {finding.issues.map((issue, idx) => (
            <IssueBadge key={idx} issue={issue} />
          ))}
        </div>
      </div>
    </Link>
  );
}

function IssueBadge({ issue }: { issue: Finding['issues'][number] }) {
  const { t } = useTranslation();
  const common = {
    fontSize: 10,
    padding: '3px 8px',
    borderRadius: 999,
    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
    fontWeight: 500,
  };
  switch (issue.kind) {
    case 'weak':
      return (
        <span
          style={{
            ...common,
            color: 'var(--c-amber)',
            background: 'var(--bg-amber)',
            border: '1px solid var(--c-amber)',
          }}
        >
          {t('admin_ui.security.badge_weak', { score: issue.score })}
        </span>
      );
    case 'duplicate':
      return (
        <span
          style={{
            ...common,
            color: 'var(--c-purple)',
            background: 'var(--bg-purple)',
            border: '1px solid var(--c-purple)',
          }}
          title={t('admin_ui.security.duplicate_title', { titles: issue.with.join(', ') })}
        >
          {t('admin_ui.security.badge_duplicate', { times: issue.with.length + 1 })}
        </span>
      );
    case 'breach':
      return (
        <span
          style={{
            ...common,
            color: 'var(--c-rose)',
            background: 'var(--bg-rose)',
            border: '1px solid var(--c-rose)',
          }}
        >
          {t('admin_ui.security.badge_breach', { hits: formatCount(issue.count) })}
        </span>
      );
    case 'expired':
      return (
        <span
          style={{
            ...common,
            color: 'var(--c-cyan)',
            background: 'var(--bg-cyan)',
            border: '1px solid var(--c-cyan)',
          }}
        >
          {t('admin_ui.security.badge_expired')}
        </span>
      );
    case 'old':
      return (
        <span
          style={{
            ...common,
            color: 'var(--text-muted)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-strong)',
          }}
        >
          {t('admin_ui.security.badge_old', { days: issue.days })}
        </span>
      );
  }
}

function formatCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
